import { ToastQueue } from "@adobe/react-spectrum";
import { AtollonEvent, publish } from "../infra/events/Events";
import { BrowserStorage } from "../services/LocalStorage/BrowserStorage";
import { IStore } from "../services/LocalStorage/IStore";
import { Application } from "../services/soap/features/responses/ListApplicationsResponse";
import * as pdfjsLib from "pdfjs-dist";
// import 'pdfjs-dist/web/pdf_viewer.css';
import { FPAData } from "../infra/protected/FPA/FPAData";
// Add URL polyfill for iPad
if (typeof window !== "undefined" && !window.URL.parse) {
  window.URL.parse = function (url: string) {
    return new URL(url, window.location.href);
  };
}

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export const getAppIndent = (applications: Application[], id: string) => {
  let selected: Application[] = applications.filter((x) => x.id === id);
  return selected[0].appIdent;
};

export const getAtollonUrl = (appIdent: string | null): string => {
  if (!appIdent) return "";

  const store: IStore = new BrowserStorage();
  const prefix: string = "atollon://";

  const customApps: string[] = store.customApps;
  const appUrls: Map<string, string> = new Map([
    ["com.atollon.messaging.messages", "messaging/openCommunication"],
    ["com.atollon.finance.invoice.issued", "finance/openInvoicesIssued"],
    ["com.atollon.finance.invoice.received", "finance/openInvoicesReceived"],
    ["com.atollon.scheduler.callCenter.outgoing", "scheduler/openCallCenter"],
    ["com.atollon.scheduler.events", "scheduler/openCalendar"],
    ["com.atollon.scheduler.tasks", "scheduler/openTasks"],
    [
      "com.atollon.lagoon.general.customApplication.serviceActivities",
      "timesheet/openTimesheetGrid",
    ],
    [
      "com.atollon.recruitment.dashboard",
      "recruitment/openRecruitmentDashboard",
    ],
    [
      "com.atollon.recruitment.foldertype.applicant",
      "recruitment/openApplicants",
    ],
    ["com.atollon.recruitment.vacancies", "recruitment/openVacancies"],
    // ['', 'logger/show'],
    // ['', 'access/openUsersSettings']
  ]);

  if (customApps.includes(appIdent))
    return prefix + "customApplication/" + appIdent;

  if (appUrls.has(appIdent)) return prefix + appUrls.get(appIdent);

  return "";
};

export const invokeFinderApp = (
  contextId: string,
  contextType: string = ""
): void => {
  publish(AtollonEvent.FINDER_DETAIL_LOADED, { contextId, contextType });
};

export const invokeContactsApp = (contactId: string): void => {
  publish(AtollonEvent.CONTACTS_APP_LOADED, { contactId });
};

export const invokeCreateContextApp = (
  contextId: string,
  contextType: string = "",
  isCopy: boolean = false,
  selectedItem: FPAData | null = null
): void => {
  publish(AtollonEvent.CREATE_CONTEXT_APP, {
    contextId,
    contextType,
    isCopy,
    selectedItem,
  });
};

export const invokeNewMessageCreating = (
  contextId: any,
  contextType: string = "",
  attachments?: any,
  notifyUsers?: any,
  /** Whether the context is a folder (not a valid project/activity/contact context) */
  isFolderContext?: boolean
): void => {
  publish(AtollonEvent.NEW_MESSAGE_CREATING, {
    contextId,
    contextType,
    attachments,
    notifyUsers,
    isFolderContext,
  });
};

export const invokeNewMessageCreated = (
  contextId: string,
  contextType: string = ""
): void => {
  //console.log('[NavigationUtils] invokeNewMessageCreated called with:', { contextId, contextType });
  //console.log('[NavigationUtils] Event name:', AtollonEvent.NEW_MESSAGE_CREATED);
  publish(AtollonEvent.NEW_MESSAGE_CREATED, { contextId, contextType });
  //console.log('[NavigationUtils] publish() completed');
};

export const invokeReplyMessage = (
  contextId: any,
  replyType: string = "",
  messageObj?: any,
  emailAttachments?: any
): void => {
  publish(AtollonEvent.REPLY_MESSAGE, {
    contextId,
    replyType,
    messageObj,
    emailAttachments,
  });
};

export const openPdfContentInNewWindow = async (content: any) => {
  const blob = new Blob([content], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);

  try {
    const pdfContainer = document.createElement("div");
    pdfContainer.style.position = "fixed";
    pdfContainer.style.top = "0";
    pdfContainer.style.left = "0";
    pdfContainer.style.width = "100%";
    pdfContainer.style.height = "100%";
    pdfContainer.style.zIndex = "9999";
    pdfContainer.style.backgroundColor = "white";
    pdfContainer.style.display = "flex";
    pdfContainer.style.flexDirection = "column";

    // Create viewer container
    const viewerContainer = document.createElement("div");
    viewerContainer.style.flex = "1";
    viewerContainer.style.overflow = "auto";
    viewerContainer.style.position = "relative";
    viewerContainer.style.padding = "20px";
    pdfContainer.appendChild(viewerContainer);

    // Create toolbar
    const toolbar = document.createElement("div");
    toolbar.style.padding = "8px";
    toolbar.style.backgroundColor = "#f0f0f0";
    toolbar.style.display = "flex";
    toolbar.style.gap = "8px";
    toolbar.style.alignItems = "center";
    toolbar.style.flexWrap = "wrap";
    toolbar.style.justifyContent = "space-between";
    toolbar.style.position = "sticky";
    toolbar.style.bottom = "0";
    toolbar.style.zIndex = "1000";
    pdfContainer.appendChild(toolbar);

    // Create page controls
    const pageControls = document.createElement("div");
    pageControls.style.display = "flex";
    pageControls.style.alignItems = "center";
    pageControls.style.gap = "4px";
    pageControls.style.flexWrap = "wrap";
    pageControls.style.flex = "1";
    pageControls.style.minWidth = "120px";

    const createButton = (text: string) => {
      const button = document.createElement("button");
      button.textContent = text;
      button.style.padding = "4px 8px";
      button.style.border = "1px solid #ccc";
      button.style.borderRadius = "4px";
      button.style.backgroundColor = "white";
      button.style.cursor = "pointer";
      button.style.fontSize = "14px";
      button.style.minWidth = "32px";
      button.style.height = "32px";
      button.style.display = "flex";
      button.style.alignItems = "center";
      button.style.justifyContent = "center";
      return button;
    };

    const prevButton = createButton("◀");
    pageControls.appendChild(prevButton);

    const pageNumber = document.createElement("span");
    pageNumber.textContent = "1/1";
    pageNumber.style.minWidth = "40px";
    pageNumber.style.textAlign = "center";
    pageNumber.style.fontSize = "14px";
    pageControls.appendChild(pageNumber);

    const nextButton = createButton("▶");
    pageControls.appendChild(nextButton);

    // Create zoom controls
    const zoomControls = document.createElement("div");
    zoomControls.style.display = "flex";
    zoomControls.style.alignItems = "center";
    zoomControls.style.gap = "4px";
    zoomControls.style.flexWrap = "wrap";
    zoomControls.style.flex = "1";
    zoomControls.style.minWidth = "120px";
    zoomControls.style.justifyContent = "flex-end";

    // Check if we're in mobile view
    const isMobileView = window.matchMedia("(max-width: 768px)").matches;

    // Declare variables outside the if block with default values
    let zoomOutButton: HTMLButtonElement = document.createElement("button");
    let zoomLevel: HTMLSpanElement = document.createElement("span");
    let zoomInButton: HTMLButtonElement = document.createElement("button");
    let fitToWidthButton: HTMLButtonElement = document.createElement("button");
    let fitToHeightButton: HTMLButtonElement = document.createElement("button");
    let fitTo100Button: HTMLButtonElement = document.createElement("button");

    if (!isMobileView) {
      zoomOutButton = createButton("−");
      zoomControls.appendChild(zoomOutButton);

      zoomLevel = document.createElement("span");
      zoomLevel.textContent = "100%";
      zoomLevel.style.minWidth = "40px";
      zoomLevel.style.textAlign = "center";
      zoomLevel.style.fontSize = "14px";
      zoomControls.appendChild(zoomLevel);

      zoomInButton = createButton("+");
      zoomControls.appendChild(zoomInButton);
    }

    // Add Fit to Width button
    fitToWidthButton = createButton("↔");
    fitToWidthButton.style.padding = "4px 8px";
    fitToWidthButton.title = "Fit to Width";
    zoomControls.appendChild(fitToWidthButton);

    // Add Fit to Height button
    fitToHeightButton = createButton("↕");
    fitToHeightButton.style.padding = "4px 8px";
    fitToHeightButton.title = "Fit to Height";
    zoomControls.appendChild(fitToHeightButton);

    // Add Fit to 100% button
    fitTo100Button = createButton("⤡");
    fitTo100Button.style.padding = "4px 8px";
    fitTo100Button.title = "Fit to 100%";
    zoomControls.appendChild(fitTo100Button);

    // Create print button
    const printButton = createButton("🖨️");
    printButton.style.padding = "4px 8px";
    printButton.title = "Print PDF";
    printButton.style.backgroundColor = "#007bff";
    printButton.style.color = "white";
    printButton.style.border = "none";
    printButton.style.marginLeft = "8px";
    printButton.style.flexShrink = "0";
    printButton.onclick = () => {
      // Create a new window for printing
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback: try to print the current window
        window.print();
      }
    };
    zoomControls.appendChild(printButton);

    // Create close button
    const closeButton = createButton("✕");
    closeButton.style.backgroundColor = "#dc3545";
    closeButton.style.color = "white";
    closeButton.style.border = "none";
    closeButton.style.marginLeft = "8px";
    closeButton.style.flexShrink = "0";
    closeButton.onclick = () => {
      document.body.removeChild(pdfContainer);
      URL.revokeObjectURL(blobUrl);
    };
    zoomControls.appendChild(closeButton);

    // Add controls to toolbar
    toolbar.appendChild(pageControls);
    toolbar.appendChild(zoomControls);

    // Load PDF document
    const loadingTask = pdfjsLib.getDocument(blobUrl);
    const pdfDocument = await loadingTask.promise;
    const totalPages = pdfDocument.numPages;
    let currentPage = 1;
    let currentScale = 2.0;

    const renderPage = async (pageNum: number, scale: number) => {
      viewerContainer.innerHTML = "";
      const page = await pdfDocument.getPage(pageNum);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Could not get canvas context");
      }

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
      viewerContainer.appendChild(canvas);
    };

    // Update page number display
    const updatePageNumber = () => {
      pageNumber.textContent = `${currentPage}/${totalPages}`;
    };

    // Initialize first page
    await renderPage(currentPage, currentScale);
    updatePageNumber();

    // Calculate scale to fit width
    const calculateFitToWidthScale = async () => {
      const page = await pdfDocument.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1.0 });
      const containerWidth = viewerContainer.clientWidth - 40; // Subtracting padding
      return containerWidth / viewport.width;
    };

    // Calculate scale to fit height
    const calculateFitToHeightScale = async () => {
      const page = await pdfDocument.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1.0 });
      const containerHeight = viewerContainer.clientHeight - 40; // Subtracting padding
      return containerHeight / viewport.height;
    };

    // Add event listeners
    prevButton.onclick = async () => {
      if (currentPage > 1) {
        currentPage--;
        await renderPage(currentPage, currentScale);
        updatePageNumber();
      }
    };

    nextButton.onclick = async () => {
      if (currentPage < totalPages) {
        currentPage++;
        await renderPage(currentPage, currentScale);
        updatePageNumber();
      }
    };

    fitToWidthButton.onclick = async () => {
      currentScale = await calculateFitToWidthScale();
      await renderPage(currentPage, currentScale);
      zoomLevel.textContent = `${Math.round(currentScale * 100)}%`;
    };

    fitToHeightButton.onclick = async () => {
      currentScale = await calculateFitToHeightScale();
      await renderPage(currentPage, currentScale);
      zoomLevel.textContent = `${Math.round(currentScale * 100)}%`;
    };

    fitTo100Button.onclick = async () => {
      currentScale = 1.0;
      await renderPage(currentPage, currentScale);
      zoomLevel.textContent = "100%";
    };

    if (!isMobileView) {
      zoomOutButton.onclick = async () => {
        if (currentScale > 0.5) {
          currentScale -= 0.1;
          await renderPage(currentPage, currentScale);
          zoomLevel.textContent = `${Math.round(currentScale * 100)}%`;
        }
      };

      zoomInButton.onclick = async () => {
        if (currentScale < 2.0) {
          currentScale += 0.1;
          await renderPage(currentPage, currentScale);
          zoomLevel.textContent = `${Math.round(currentScale * 100)}%`;
        }
      };
    }

    document.body.appendChild(pdfContainer);
  } catch (error) {
    console.error("Error rendering PDF:", error);
    URL.revokeObjectURL(blobUrl);
  }
};

export const openUrlInNewWindow = (url: string): void => {
  try {
    window.open(
      url,
      "_blank",
      "scrollbars=yes,status=yes,menubar=no,toolbar=no"
    );
  } catch (error) {
    console.error("Error opening document:", error);
    ToastQueue.negative(
      "Failed to open document. Please try again or check your browser settings.",
      { timeout: 3000 }
    );
  }
};
